import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { toast } from 'sonner';

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';
const readOnlyCls = 'w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm cursor-not-allowed';

/** Job numbers are zero-padded to 4 by the API's job-label generator. */
const JOB_PADDING = 4;

/**
 * Envelope-safe unwrap. `res.data?.data || res.data` reads the ENVELOPE as data
 * when the API answers `{success: true, data: null}` — see CLAUDE.md. This
 * distinguishes "envelope carrying absent data" from "the data".
 */
function unwrap(body: any): any {
  if (body && typeof body === 'object' && 'data' in body) return body.data;
  return body;
}

export default function JobNumberingPage() {
  const queryClient = useQueryClient();
  // PATCH /settings/company is requireRole('admin'), non-strict.
  const canWrite = useCanWriteCompanySettings();

  // Job numbering lives on the legacy GET /settings/company section endpoint —
  // settings-v2 has no job-numbering section. `jobNumbering.nextNumber` there is
  // company_job_counters.last_number + 1, the counter the create path actually
  // consumes. companies.next_job_number is vestigial and is NOT what this reads.
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'company'],
    queryFn: () => apiClient.get('/settings/company').then(r => unwrap(r.data) ?? {}),
  });

  const [prefix, setPrefix] = useState('J-');
  const [dirty, setDirty] = useState(false);

  const nextNumber: number | null =
    typeof data?.jobNumbering?.nextNumber === 'number' ? data.jobNumbering.nextNumber : null;

  useEffect(() => {
    if (data?.jobNumbering) {
      setPrefix(data.jobNumbering.prefix ?? 'J-');
      setDirty(false);
    }
  }, [data]);

  const saveMutation = useMutation({
    // Only the prefix is sent. The counter is advanced by the job create path;
    // echoing it back here would let a stale read walk it backwards.
    mutationFn: () => apiClient.patch('/settings/company', { jobNumbering: { prefix } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'company'] });
      // Company-state mutation: without these the change isn't live until a hard refresh.
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['companies', 'me'] });
      setDirty(false);
      toast.success('Job numbering saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  return (
    <div className="min-h-full">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-4">
        {canWrite ? (
          <div className="flex justify-end">
            <button
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveMutation.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted">Only an admin can change job numbering.</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-white block mb-1">Job Prefix</label>
            <input
              className={inputCls}
              value={prefix}
              disabled={!canWrite}
              aria-label="Job Prefix"
              onChange={e => { setPrefix(e.target.value); setDirty(true); }}
              placeholder="J-"
            />
            <p className="text-xs text-fieldiq-muted mt-1">
              Preview:{' '}
              <span className="text-fieldiq-accent font-mono">
                {nextNumber === null ? '—' : `${prefix}${String(nextNumber).padStart(JOB_PADDING, '0')}`}
              </span>
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-white block mb-1">Next Job Number</label>
            <input className={readOnlyCls} value={nextNumber === null ? '—' : nextNumber} readOnly disabled />
            <p className="text-xs text-fieldiq-muted mt-1">
              Advances automatically each time a job is created.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
